export interface SavedSession {
  sessionId: string;
  adventureNumber: number;
  chapterNumber: number;
  chapterTitle: string;
  lastPlayedAt: number;
}

const STORAGE_KEY = 'percy_saved_sessions';
const MAX_SAVES = 3;

export const isLocalStorageAvailable = (): boolean => {
  try {
    const testKey = '__percy_storage_test__';
    localStorage.setItem(testKey, '1');
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

interface LegacySavedSession {
  sessionId: string;
  name: string;
  chapterNumber?: number;
  chapterTitle?: string;
  timestamp: number;
  previewText: string;
}

const migrateLegacySaves = (raw: unknown[]): SavedSession[] => {
  return raw.map((item, index) => {
    const s = item as Record<string, unknown>;
    if ('adventureNumber' in s && 'lastPlayedAt' in s) {
      return s as unknown as SavedSession;
    }
    const legacy = s as unknown as LegacySavedSession;
    return {
      sessionId: legacy.sessionId,
      adventureNumber: index + 1,
      chapterNumber: legacy.chapterNumber ?? 1,
      chapterTitle: legacy.chapterTitle ?? 'Chapter 1',
      lastPlayedAt: legacy.timestamp ?? Date.now(),
    };
  });
};

export const getSavedSessions = (): SavedSession[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];
    const sessions = migrateLegacySaves(parsed);
    return sessions.sort((a, b) => b.lastPlayedAt - a.lastPlayedAt);
  } catch {
    return [];
  }
};

const persistSessions = (sessions: SavedSession[]): void => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
};

export const getNextAdventureNumber = (): number => {
  const sessions = getSavedSessions();
  const used = new Set(sessions.map(s => s.adventureNumber));
  for (let i = 1; i <= MAX_SAVES; i++) {
    if (!used.has(i)) return i;
  }
  return MAX_SAVES + 1;
};

export const upsertSave = (session: SavedSession): void => {
  try {
    const sessions = getSavedSessions();
    const existingIndex = sessions.findIndex(s => s.sessionId === session.sessionId);
    if (existingIndex >= 0) {
      sessions[existingIndex] = session;
    } else {
      sessions.push(session);
    }
    while (sessions.length > MAX_SAVES) {
      const oldestIdx = sessions.reduce(
        (minIdx, s, i, arr) => s.lastPlayedAt < arr[minIdx].lastPlayedAt ? i : minIdx,
        0
      );
      sessions.splice(oldestIdx, 1);
    }
    persistSessions(sessions);
  } catch {
    // Silent failure (private browsing, storage full, etc.)
  }
};

export const removeSave = (sessionId: string): void => {
  try {
    const sessions = getSavedSessions().filter(s => s.sessionId !== sessionId);
    persistSessions(sessions);
  } catch {
    // Silent failure
  }
};

export const getOldestSave = (): SavedSession | null => {
  const sessions = getSavedSessions();
  if (sessions.length === 0) return null;
  return sessions.reduce((oldest, s) => s.lastPlayedAt < oldest.lastPlayedAt ? s : oldest);
};

export const formatRelativeTime = (timestamp: number): string => {
  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  const isToday = now.toDateString() === date.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = yesterday.toDateString() === date.toDateString();

  if (isToday) return 'Played today';
  if (isYesterday) return 'Played yesterday';
  if (diffDays < 7) return `Played ${diffDays} days ago`;
  return `Played ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
};
